import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, fmtTime, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../components/Layout";
import ChatThread from "../components/ChatThread";
import ContextPanel from "../components/ContextPanel";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "unassigned", label: "Unassigned" },
  { id: "sla", label: "SLA ⚠" },
  { id: "resolved", label: "Resolved" },
];

const isSlaBreached = (c) => c.status === "open" && Date.now() - new Date(c.created_at).getTime() > 48 * 3600 * 1000;
const isSlaRisk = (c) => { const age = Date.now() - new Date(c.created_at).getTime(); return c.status === "open" && age > 36 * 3600 * 1000 && age <= 48 * 3600 * 1000; };

export default function Inbox() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const [convs, setConvs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const { subscribe } = useSocket();
  const { user } = useAuth();
  const [sp] = useSearchParams();

  useEffect(() => {
    if (user?.role === "admin") api.get("/agents").then((r) => setAgents(r.data));
  }, [user?.role]);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/conversations", { params: { filter, search, agent_id: agentFilter } });
    setConvs(data);
  }, [filter, search, agentFilter]);

  const loadDetail = useCallback(async (id) => {
    const { data } = await api.get(`/conversations/${id}`);
    setDetail(data);
    api.post(`/conversations/${id}/seen`).catch(() => {});
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const cid = sp.get("c");
    if (cid) setSelectedId(cid);
  }, [sp]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId, loadDetail]);

  useEffect(() => subscribe((data) => {
    if (data.event === "conversation") {
      const c = data.conversation;
      let matches = filter === "all" ? true
        : filter === "mine" ? c.assigned_to === user?.id && c.status === "open"
        : filter === "unassigned" ? !c.assigned_to && c.status === "open"
        : filter === "sla" ? (isSlaBreached(c) || isSlaRisk(c))
        : filter === "resolved" ? c.status === "resolved" : true;
      if (agentFilter && c.assigned_to !== agentFilter) matches = false;
      setConvs((prev) => {
        const rest = prev.filter((x) => x.id !== c.id);
        return matches ? [c, ...rest].sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || "")) : rest;
      });
      if (c.id === selectedId) {
        setDetail((d) => (d ? { ...d, conversation: c } : d));
      }
    }
    if (data.event === "message" && data.conversation_id === selectedId) {
      setDetail((d) => {
        if (!d || d.messages.some((m) => m.id === data.message.id)) return d;
        return { ...d, messages: [...d.messages, data.message] };
      });
      if (data.message.sender === "customer") api.post(`/conversations/${selectedId}/seen`).catch(() => {});
    }
    if (data.event === "message_update" && data.conversation_id === selectedId) {
      setDetail((d) => (d ? { ...d, messages: d.messages.map((m) => (m.id === data.message.id ? data.message : m)) } : d));
    }
    if (data.event === "seen" && data.by === "customer" && data.conversation_id === selectedId) {
      setDetail((d) => d ? { ...d, messages: d.messages.map((m) => (m.sender !== "customer" ? { ...m, status: "seen" } : m)) } : d);
    }
  }), [subscribe, selectedId, filter, agentFilter, user?.id]);

  return (
    <div className="flex h-full">
      {/* Left pane */}
      <div className="w-80 shrink-0 border-r-2 border-[#131E3A] flex flex-col">
        <div className="p-3 space-y-2 border-b-2 border-dashed border-[#131E3A]/30">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="conversation-search-input" className="sd-input pl-8 py-2 text-sm" placeholder="Search name, phone, ticket…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.id} data-testid={`filter-${f.id}`} className={`filter-pill ${filter === f.id ? "active" : ""}`} onClick={() => setFilter(f.id)}>{f.label}</button>
            ))}
          </div>
          {user?.role === "admin" && (
            <select data-testid="agent-filter-select" className="sd-input py-1.5 text-xs" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
              <option value="">All agents</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conversation-list">
          {convs.length === 0 && <p className="text-center text-sm text-slate-500 mt-8 font-script text-xl">no conversations yet…</p>}
          {convs.map((c) => (
            <div key={c.id} data-testid={`conversation-item-${c.id}`} onClick={() => setSelectedId(c.id)}
              className={`conv-item p-2.5 ${selectedId === c.id ? "selected" : ""}`}>
              <div className="flex justify-between items-center">
                <span className="font-heading font-bold text-sm truncate">{c.customer_name}</span>
                <span className="text-[10px] text-slate-500 shrink-0">{fmtDate(c.last_message_at)} {fmtTime(c.last_message_at)}</span>
              </div>
              <div className="flex justify-between items-center mt-0.5 gap-2">
                <span className="text-xs text-slate-600 truncate">{c.last_message_preview || "New chat"}</span>
                {c.unread_agent > 0 && <span className="bg-[#FFD1DC] border border-[#131E3A] text-[10px] font-bold rounded-full px-1.5 shrink-0">{c.unread_agent}</span>}
              </div>
              <div className="flex gap-1.5 mt-1 items-center flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider">{c.ticket_number}</span>
                <span className={`text-[10px] font-bold px-1.5 rounded border border-[#131E3A] ${c.status === "open" ? "bg-[#D4F0F0]" : "bg-[#E8DFF5]"}`}>{c.status}</span>
                {isSlaBreached(c) && <span data-testid={`sla-badge-${c.id}`} className="text-[10px] font-bold px-1.5 rounded border border-red-600 bg-red-100 text-red-700">⚠ SLA</span>}
                {isSlaRisk(c) && <span data-testid={`sla-risk-badge-${c.id}`} className="text-[10px] font-bold px-1.5 rounded border border-amber-600 bg-amber-100 text-amber-700">⏳ 36h+</span>}
                {c.assigned_name ? <span className="text-[10px] text-slate-500 truncate">→ {c.assigned_name}</span> : <span className="text-[10px] text-red-600 font-semibold">unassigned</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Center pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {detail ? <ChatThread detail={detail} reload={() => loadDetail(selectedId)} /> : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <p className="font-script text-3xl text-amber-800">pick a conversation ✦</p>
            <p className="text-sm text-slate-500 mt-2">Select a chat from the list to start replying</p>
          </div>
        )}
      </div>
      {/* Right pane */}
      {detail && <ContextPanel detail={detail} onCustomerUpdate={(cust) => setDetail((d) => ({ ...d, customer: cust }))} />}
    </div>
  );
}
